import { Component, OnInit, Inject, ChangeDetectorRef } from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material';
import { PolicyService } from '@app/api/api';
import { SlugPipe } from '@app/core/_base/layout';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'modal-create-posts-policy',
  templateUrl: './modal-create-posts-policy.component.html',
  styleUrls: ['./modal-create-posts-policy.component.scss'],
})
export class ModalCreatePostsPolicyComponent implements OnInit {
  isEdit: boolean = false;
  hasFormErrors: boolean = false;

  // --- 1. Form Tiếng Việt (Gốc) ---
  policyForm = new FormGroup({
    title: new FormControl('', [Validators.required]),
    slug: new FormControl(''),
    content: new FormControl('', [Validators.required]),
    description: new FormControl('', [Validators.required]),
    updated_at: new FormControl(''),
    created_at: new FormControl(''),
  });

  // --- 2. Form Tiếng Anh ---
  policyFormEng = new FormGroup({
    title: new FormControl('', [Validators.required]),
    content: new FormControl('', [Validators.required]),
    description: new FormControl('', [Validators.required]),
  });
  isUpdateLangEng: boolean = false;
  idPolicyEng: string = '';

  // --- 3. Form Tiếng Trung (Mới thêm) ---
  policyFormCn = new FormGroup({
    title: new FormControl('', [Validators.required]),
    content: new FormControl('', [Validators.required]),
    description: new FormControl('', [Validators.required]),
  });
  isUpdateLangCn: boolean = false;
  idPolicyCn: string = '';

  constructor(
    public dialogRef: MatDialogRef<ModalCreatePostsPolicyComponent>,
    @Inject(MAT_DIALOG_DATA) public data,
    private changeDetectorRef: ChangeDetectorRef,
    private policyService: PolicyService,
    private slugPipe: SlugPipe,
    private toastr: ToastrService
  ) {}

  ngOnInit() {
    if (this.data.id != null) {
      this.isEdit = true;
      this.getPolicyById(this.data.id);
    }
  }

  // Cấu hình Editor
  config = {
    placeholder: 'Nhập nội dung chi tiết',
    tabsize: 2,
    height: 'auto',
    dialogsInBody: true,
    minHeight: '35vh',
    uploadImagePath: '/api/upload',
    focus: false,
    fontNamesIgnoreCheck: ['Helvetica', 'Arial', 'Roboto', 'Times'],
    toolbar: [
      ['misc', ['codeview', 'undo', 'redo']],
      ['font', ['bold', 'italic', 'underline', 'clear']],
      ['fontsize', ['fontname', 'fontsize', 'color']],
      ['para', ['style', 'ul', 'ol', 'paragraph', 'height']],
      ['insert', ['table', 'picture', 'link', 'video', 'hr']],
    ],
  };

  getPolicyById(id) {
    this.policyService.apiPoliciesIdGet(id).subscribe((res) => {
      if (res.success) {
        // 1. Fill Tiếng Việt
        this.policyForm.patchValue({
          title: res.data.title,
          slug: res.data.slug,
          content: res.data.content,
          description: res.data.description,
        });

        // 2. Fill Tiếng Anh (nếu có)
        // Lưu ý: Kiểm tra key trả về từ API, thường là extend_policy_en hoặc tương tự
        if (res.data.extend_policy_en) {
          this.isUpdateLangEng = true;
          this.idPolicyEng = res.data.extend_policy_en.id;
          this.policyFormEng.patchValue({
            title: res.data.extend_policy_en.title,
            description: res.data.extend_policy_en.description,
            content: res.data.extend_policy_en.content,
          });
        }

        // 3. Fill Tiếng Trung (nếu có)
        // Lưu ý: Kiểm tra key trả về từ API, thường là extend_policy_zh (dựa theo edit-product dùng productZh)
        if (res.data.extend_policy_zh) {
          this.isUpdateLangCn = true;
          this.idPolicyCn = res.data.extend_policy_zh.id;
          this.policyFormCn.patchValue({
            title: res.data.extend_policy_zh.title,
            description: res.data.extend_policy_zh.description,
            content: res.data.extend_policy_zh.content,
          });
        }
      }
    });
  }

  generateSlug() {
    const title = this.policyForm.value.title;
    const slug = this.slugPipe.transform(title);
    this.policyForm.get('slug').setValue(slug);
  }

  save() {
    this.hasFormErrors = true;

    // Validate Form Tiếng Việt là bắt buộc
    if (this.policyForm.invalid) {
      this.policyForm.markAllAsTouched();
      this.toastr.error('Vui lòng nhập đầy đủ thông tin Tiếng Việt');
      return;
    }

    if (this.isEdit) {
      this.update();
    } else {
      this.create();
    }
  }

  create() {
    this.generateSlug();
    this.policyForm.patchValue({
      updated_at: new Date().toISOString(),
      created_at: new Date().toISOString(),
    });

    this.policyService.apiPoliciesPost(this.policyForm.value).subscribe((res) => {
      if (res.success) {
        // Sau khi tạo tiếng Việt thành công, xử lý tiếp các ngôn ngữ khác
        this.processExtraLanguages(res.data.id);
      } else {
        this.toastr.error('Tạo mới thất bại');
      }
    });
  }

  update() {
    this.generateSlug();
    this.policyForm.patchValue({ updated_at: new Date().toISOString() });

    this.policyService.apiPoliciesIdPut(this.data.id, this.policyForm.value).subscribe((res) => {
      if (res.success) {
        // Sau khi update tiếng Việt thành công, xử lý tiếp các ngôn ngữ khác
        this.processExtraLanguages(this.data.id);
      } else {
        this.toastr.error('Cập nhật thất bại');
      }
    });
  }

  // Hàm xử lý lưu song song cả Anh và Trung sau khi đã có ID bài viết gốc
  async processExtraLanguages(parentId: string) {
    const promises = [];

    // --- Xử lý Tiếng Anh ---
    if (this.policyFormEng.dirty && this.policyFormEng.valid) {
      const payloadEn = { ...this.policyFormEng.value, policy_id: parentId };
      if (this.isUpdateLangEng) {
        // Gọi API update (giả định tên hàm, bạn cần check service thực tế)
        // promises.push(this.policyService.apiPoliciesLangPut('policyEn', this.idPolicyEng, payloadEn).toPromise());
        console.log('Update EN:', payloadEn);
      } else {
        // Gọi API create
        // promises.push(this.policyService.apiPoliciesLangPost('policyEn', payloadEn).toPromise());
        console.log('Create EN:', payloadEn);
      }
    }

    // --- Xử lý Tiếng Trung ---
    if (this.policyFormCn.dirty && this.policyFormCn.valid) {
      const payloadCn = { ...this.policyFormCn.value, policy_id: parentId };
      if (this.isUpdateLangCn) {
        // Gọi API update (thường dùng mã 'policyZh' giống productZh)
        // promises.push(this.policyService.apiPoliciesLangPut('policyZh', this.idPolicyCn, payloadCn).toPromise());
        console.log('Update CN:', payloadCn);
      } else {
        // Gọi API create
        // promises.push(this.policyService.apiPoliciesLangPost('policyZh', payloadCn).toPromise());
        console.log('Create CN:', payloadCn);
      }
    }

    // Đợi tất cả (nếu có API thật) hoặc đóng modal luôn
    // await Promise.all(promises);

    this.toastr.success('Lưu dữ liệu thành công');
    this.dialogRef.close({ event: true });
  }

  closeModal() {
    this.dialogRef.close();
  }
}
